import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalVariant,
  Stack,
  Title,
} from '@patternfly/react-core';
import { useFeature } from '@ole-ui/ole-ui-components';

import { EnablementFeatureFlags } from 'config/featureFlags';
import { resetSavedAppData } from 'services/ServiceWorkerService';

import './AdvancedSettings.scss';

const AdvancedSettings = () => {
  const { t } = useTranslation();
  const isEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.AdvancedSettingsEnabled,
    defaultFlagValue: false,
  });
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);

  if (!isEnabled) {
    return null;
  }

  const handleCloseModal = () => {
    if (!refreshingData) {
      setShowRefreshModal(false);
    }
  };

  const handleRefreshSavedData = async () => {
    setRefreshingData(true);
    try {
      await resetSavedAppData();
    } finally {
      setRefreshingData(false);
    }
  };

  return (
    <>
      <section className="profile-advanced-settings">
        <Stack hasGutter>
          <Title headingLevel="h3" size="2xl">
            {t('Advanced settings')}
          </Title>
          <p className="profile-advanced-settings-description">
            {t(
              'Having trouble with outdated or stuck content? Use the option below to refresh saved RHLS data on this device.',
            )}
          </p>
          <div>
            <Button
              variant="secondary"
              isDanger
              onClick={() => setShowRefreshModal(true)}
            >
              {t('Clear saved app data')}
            </Button>
          </div>
        </Stack>
      </section>

      <Modal
        variant={ModalVariant.small}
        title={t('Clear saved app data?')}
        isOpen={showRefreshModal}
        onClose={handleCloseModal}
        actions={[
          <Button
            key="confirm"
            variant="danger"
            onClick={handleRefreshSavedData}
            isLoading={refreshingData}
            isDisabled={refreshingData}
          >
            {t('Clear data')}
          </Button>,
          <Button
            key="cancel"
            variant="link"
            onClick={handleCloseModal}
            isDisabled={refreshingData}
          >
            {t('Cancel')}
          </Button>,
        ]}
      >
        <Stack hasGutter className="profile-advanced-settings-modal-body">
          <p>
            {t(
              'This removes RHLS data saved on this device to speed up loading, such as catalog and other stored content.',
            )}
          </p>
          <div>
            <p className="profile-advanced-settings-modal-list-label">
              {t('This will not:')}
            </p>
            <ul className="profile-advanced-settings-modal-list">
              <li>{t('Sign you out')}</li>
              <li>{t('Reset your account settings or preferences')}</li>
              <li>{t('Clear browsing data for other websites')}</li>
            </ul>
          </div>
        </Stack>
      </Modal>
    </>
  );
};

export default AdvancedSettings;
